import React, { useState, useEffect, useCallback } from "react";

import { CarbonFootprintResult } from "libLegacy/api/models";
import {
  fetchCarbonFootprint,
  fetchDecarbProfileUrl,
} from "libLegacy/api/dashboard";

import { useToken, useUserContext } from "contexts/UserContext";
import { useSetError } from "contexts/ErrorContext";
import { PageStandard } from "components/admin/PageStandard";
import { CarbonFootprint } from "./CarbonFootprint";
import { Grid, Skeleton } from "@mui/material";
import { DecarbonizationProfile } from "./DecarbonizationProfile";
import { BlockStandard } from "components/admin/BlockStandard";
import { RetroCTA } from "components/retrospective/RetroCTA";

export const DecarbonizationMain = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [profileUrl, setProfileUrl] = useState<string>();
  const [footprint, setFootprint] = useState<CarbonFootprintResult | false>();
  const { currentSpace } = useUserContext();
  const token = useToken();
  const setGlobalError = useSetError();

  const loadProfile = useCallback(
    async (id: string) => {
      try {
        const userUrl = await fetchDecarbProfileUrl(token);
        if (userUrl) {
          setProfileUrl(userUrl);
        }
      } catch (e) {
        // @ts-expect-error
        setGlobalError(e.response?.data?.error);
      } finally {
        setIsLoading(false);
      }
    },
    [token, setGlobalError]
  );

  const loadFootprint = useCallback(
    async (id: string) => {
      try {
        const data = await fetchCarbonFootprint(token, id);
        setFootprint(data);
      } catch (error) {
        // @ts-expect-error
        setGlobalError(error.response?.data?.error);
      } finally {
        setIsLoading(false);
      }
    },
    [token, setGlobalError]
  );

  useEffect(() => {
    setProfileUrl("");
    setFootprint(false);
    setIsLoading(true);

    if (currentSpace?.hasDiscarbonizationProfile) {
      loadProfile(currentSpace.id);
    } else {
      loadFootprint(currentSpace?.id || "");
    }
  }, [currentSpace, loadFootprint, loadProfile]);

  return (
    <PageStandard
      title="Descarbonização"
      subtitle="Veja o impacto positivo que você gerou no meio ambiente."
      displaySelector
    >
      <BlockStandard hideDivider>
        <RetroCTA />
      </BlockStandard>
      {isLoading && (
        <Skeleton
          variant="rectangular"
          width="80%"
          height="30%"
          sx={{ position: "relative", top: "35%", left: "10%", maxWidth: 480 }}
        />
      )}
      <Grid sx={{ flex: 1 }}>
        {!isLoading && profileUrl && (
          <DecarbonizationProfile url={profileUrl} />
        )}
        {!isLoading && footprint && <CarbonFootprint info={footprint} />}
      </Grid>
    </PageStandard>
  );
};
